import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { ListingsRequestBuilder } from 'src/services/listings-api/builders/ListingsRequestBuilder';
import { ListingsRequestFacetBuilder } from 'src/services/listings-api/builders/ListingsRequestFacetBuilder';
import {
  ListingsRequestAndFilterBuilder,
  ListingsRequestRangeFilterBuilder,
  ListingsRequestTextFilterBuilder,
} from 'src/services/listings-api/builders/ListingsRequestFilterBuilder';
import type { ListingsAPIRequestBody } from 'src/services/listings-api/types/ListingsAPIRequestBody';

export const adaptStoreToRequestBody = (state: ReduxApplicationState): ListingsAPIRequestBody => {
  const { productsQuery, userConfiguration } = state;
  const listingsRequestBody = new ListingsRequestBuilder()
    .currency(userConfiguration.currencyCode)
    .pageSize(10);

  if (productsQuery.canonicalFilters.searchTerm) {
    listingsRequestBody.searchTerm(productsQuery.canonicalFilters.searchTerm);
  }

  if (productsQuery.facets) {
    productsQuery.facets.forEach((facet) => {
      const facetBuilder = new ListingsRequestFacetBuilder(facet.key);

      if (facet.valueType === 'numeric') {
        facet.options.forEach((option) => {
          facetBuilder.addInterval((f) => {
            if (option.minValue !== null) {
              f.min(option.minValue);
            }
            if (option.maxValue !== null) {
              f.max(option.maxValue);
            }
          });
        });
      }

      listingsRequestBody.addFacetSpec(facetBuilder);
    });

    const facetsWithActiveOptions = productsQuery.facets.filter((facet) => {
      return facet.options.some((option) => option.active);
    });

    if (facetsWithActiveOptions.length > 0) {
      const andFilter = new ListingsRequestAndFilterBuilder();

      facetsWithActiveOptions.forEach((facet) => {
        if (facet.valueType === 'numeric') {
          const filter = new ListingsRequestRangeFilterBuilder(facet.key);

          facet.options.forEach((option) => {
            if (option.minValue !== null || option.maxValue !== null) {
              if (option.minValue !== null) {
                filter.min(option.minValue);
              }
              if (option.maxValue !== null) {
                filter.max(option.maxValue);
              }
            }
          });
          andFilter.addFilter(filter);
        } else {
          const filter = new ListingsRequestTextFilterBuilder(
            facet.key,
            facet.options.filter((option) => option.active).map((option) => String(option.value)),
          );
          andFilter.addFilter(filter);
        }
      });

      listingsRequestBody.filter(andFilter);
    }
  }

  const activeSortOption = (productsQuery.sortOptions || []).find((option) => option.active);

  switch (activeSortOption?.id) {
    case 'priceAsc':
      listingsRequestBody.sortBy('price', true);
      break;
    case 'priceDesc':
      listingsRequestBody.sortBy('price', false);
      break;
    case 'newest':
      listingsRequestBody.sortBy('attributes.published_at', false);
      break;
    case 'relevance':
    default:
      // relevance is the default sort, so does not need to be set explicitly
      break;
  }

  return listingsRequestBody.build();
};
